/* Mobile info sheet. Opens the FSA/HSA and Autoship explanations as an Amazon-style slide-up from
   the bottom of the screen instead of a tooltip anchored to the trigger (owner 2026-09-06).

   See llm-info-sheet.css for WHY: the tooltips lived inside .card__fsa, whose z-index is boxed
   between the card's quick-add button (3) and the sticky Add-to-cart bar (5), so card chrome covered
   them. This element is a child of <body> — nothing to escape, nothing to be clipped by.

   Content is CLONED from the existing tooltip markup rather than duplicated here, so the wording
   stays owned by the Liquid that renders it and cannot drift between the sheet and the desktop
   tooltip. The tooltip's own "× close" button is stripped — the sheet has its own. */
(function () {
  if (window.__llmInfoSheet) return;
  window.__llmInfoSheet = true;

  /* Decided by what the customer actually DID, not by what the device claims to be.

     ⚠️ Two device queries were tried first and BOTH broke desktop, each by hiding the tooltip while
     the sheet also refused to open — the badge then did nothing at all (owner 2026-09-06, twice):
       1. `max-width: 749.98px` — CSS pixels shrink with browser zoom, so a zoomed desktop window
          reports under 750 and looks like a phone.
       2. `(hover: none), (pointer: coarse)` — a desktop with a touchscreen reports a coarse primary
          pointer, so it looks like a phone too.
     A hybrid machine genuinely has both inputs, so no static query can answer this. A pointerdown
     tells us which one is in use right now, and it fires before the click that follows it.

     Once a touch is seen the class goes on <html>, which is what hides the tooltips (see the CSS).
     Nothing is hidden until then, so a mouse user is never left with neither. */
  var touched = false;
  document.addEventListener('pointerdown', function (e) {
    if (touched) return;
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
    touched = true;
    document.documentElement.classList.add('llm-uses-sheet');
  }, true);

  function usesSheet() { return touched; }

  /* Trigger -> where its copy lives, and what to title the sheet.
     `tip` is resolved relative to the trigger, because a card and the PDP nest these differently. */
  var SOURCES = [
    {
      trigger: '.llm-fsa-badge--interactive',
      tip: '.llm-fsa-badge__tip',
      title: 'FSA/HSA eligible'
    },
    {
      trigger: '.llm-card-autoship__info',
      tip: '.llm-card-autoship__tip',
      // The tip is a SIBLING inside .llm-card-autoship__text, not a descendant of the trigger.
      scope: '.llm-card-autoship',
      title: 'Autoship & Save'
    },
    {
      // The "i" beside Estimated delivery on the PDP (owner 2026-09-06 — it was still opening as a
      // tooltip). Its popup is also a SIBLING, inside the shared #custom_message wrapper.
      trigger: '.info-ship',
      tip: '.popup-box-message',
      scope: '.custom_message',
      title: 'Estimated delivery'
    }
  ];

  var el = null;      // { scrim, sheet, panel, titleEl, bodyEl }
  var lastFocus = null;

  function build() {
    if (el) return el;

    var scrim = document.createElement('div');
    scrim.className = 'llm-info-sheet__scrim';

    var sheet = document.createElement('div');
    sheet.className = 'llm-info-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-labelledby', 'llm-info-sheet-title');
    /* Focus lands HERE on open, not on the × button. Focusing the × made Chrome's :focus-visible
       heuristic draw a ring around it, which read as "a visible box border around the close X" on
       the delivery sheet and not the others, purely because focus arrived from a different kind of
       trigger (owner 2026-09-06). A tabindex="-1" container takes focus without a ring, which is the
       normal dialog pattern — and the × keeps its ring for real keyboard users, who should see it. */
    sheet.setAttribute('tabindex', '-1');

    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'llm-info-sheet__x';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML = '&times;';

    var panel = document.createElement('div');
    panel.className = 'llm-info-sheet__panel';

    var titleEl = document.createElement('h2');
    titleEl.className = 'llm-info-sheet__title';
    titleEl.id = 'llm-info-sheet-title';

    var bodyEl = document.createElement('div');
    bodyEl.className = 'llm-info-sheet__body';

    panel.appendChild(titleEl);
    panel.appendChild(bodyEl);
    sheet.appendChild(x);
    sheet.appendChild(panel);
    document.body.appendChild(scrim);
    document.body.appendChild(sheet);

    x.addEventListener('click', close);
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el && !el.sheet.hidden) close();
    });

    el = { scrim: scrim, sheet: sheet, panel: panel, titleEl: titleEl, bodyEl: bodyEl };
    hide();
    return el;
  }

  function hide() {
    el.scrim.hidden = true;
    el.sheet.hidden = true;
  }

  function open(title, html) {
    build();
    el.titleEl.textContent = title;
    el.bodyEl.innerHTML = html;
    /* Strip the tooltip's own close control — the sheet provides one. Two passes because the three
       sources do not share a class: the FSA and Autoship tips use named classes, while the delivery
       popup just opens with a "× close" link. Matching on the text catches that one without having
       to hardcode yet another selector, and is bounded to elements that contain nothing else. */
    var stray = el.bodyEl.querySelectorAll('.llm-fsa-badge__tip-close, .llm-card-autoship__tip-close');
    for (var i = 0; i < stray.length; i++) stray[i].remove();
    var candidates = el.bodyEl.querySelectorAll('a, button, span, div');
    for (var j = 0; j < candidates.length; j++) {
      if (/^\s*[×xX✕]\s*close\s*$/.test(candidates[j].textContent || '')) candidates[j].remove();
    }

    /* Re-append to the END of <body> on every open. The Reamaze chat launcher pins itself to the
       2147483647 maximum with an inline style, so we cannot outrank it numerically — at an equal
       z-index the LAST element in document order wins, and this guarantees that is us however late
       the chat widget (or any other app) mounts. Verified: with the sheet open, elementFromPoint over
       the chat bubble returns the sheet, not the chat.
       An `html.llm-sheet-locked #reamaze-widget { z-index: … !important }` rule was tried first and
       did NOT take — the widget keeps re-asserting its own inline value — which is why this is
       handled in DOM order instead. */
    document.body.appendChild(el.scrim);
    document.body.appendChild(el.sheet);

    el.scrim.hidden = false;
    el.sheet.hidden = false;
    lastFocus = document.activeElement;

    /* The element was `hidden` until a moment ago, so the transform transition has no start value to
       animate from yet. Force a style/layout flush by READING a layout property, then add .is-open in
       the same task — the browser now has a painted starting position and animates to it.

       Deliberately NOT requestAnimationFrame: rAF does not fire in a background or unfocused tab, so
       the sheet would render permanently off-screen there (caught in testing — everything else about
       the sheet was correct and only .is-open was missing). A reflow read has no such dependency. */
    void el.sheet.offsetHeight;
    el.scrim.classList.add('is-open');
    el.sheet.classList.add('is-open');

    document.documentElement.classList.add('llm-sheet-locked');
    el.sheet.focus({ preventScroll: true });
  }

  function close() {
    if (!el || el.sheet.hidden) return;
    el.scrim.classList.remove('is-open');
    el.sheet.classList.remove('is-open');
    document.documentElement.classList.remove('llm-sheet-locked');

    var done = function () { hide(); };
    // Hide only after the slide-down finishes, or immediately when motion is reduced.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 300);

    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  function sourceFor(target) {
    for (var i = 0; i < SOURCES.length; i++) {
      var s = SOURCES[i];
      var trig = target.closest(s.trigger);
      if (!trig) continue;
      var root = s.scope ? trig.closest(s.scope) : trig;
      var tip = root && root.querySelector(s.tip);
      if (!tip) continue;
      return { title: s.title, html: tip.innerHTML };
    }
    return null;
  }

  /* Capture phase, and both preventDefault and stopPropagation: these triggers sit inside the card's
     stretched link (.card-link::after covers the whole card), so without this a tap opens the product
     page instead of the sheet. */
  document.addEventListener('click', function (e) {
    if (!usesSheet()) return;
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.llm-info-sheet')) return;      // clicks inside the sheet itself
    var src = sourceFor(e.target);
    if (!src) return;
    e.preventDefault();
    e.stopPropagation();
    open(src.title, src.html);
  }, true);
})();
